import React, { useState } from 'react';
import { Button, Card, Input, List, Popconfirm, Space, Typography, Upload, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useAIBuilderApi } from '../../../api/aibuilder/aibuilder';
import { TDataset } from '../../../models/aibuilder/aibuilder';
import { getImagePath } from '../../../utils/format/valueFormat';
import { resizeImageFile } from '../engine/resizeImage';

export interface DatasetStationProps {
  dataset: TDataset;
  onChanged: () => void;
  editable: boolean;
}

const MAX_SAMPLES_PER_DATASET = 60;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_TEXT_LENGTH = 200;

const DatasetStation: React.FC<DatasetStationProps> = ({ dataset, onChanged, editable }) => {
  const { createLabel, updateLabel, deleteLabel, createSampleImage, createSampleText, deleteSample } =
    useAIBuilderApi();
  const isText = dataset.data_type === 'text';
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [sentenceDrafts, setSentenceDrafts] = useState<Record<number, string>>({});
  const sampleCount = (dataset.samples || []).length;
  const atQuota = sampleCount >= MAX_SAMPLES_PER_DATASET;
  const addLabel = async () => {
    if (!name.trim()) return;
    await createLabel(dataset.id, { name: name.trim(), position: dataset.labels?.length || 0 });
    setName('');
    onChanged();
  };
  const upload = async (labelId: number, file: File) => {
    const resized = await resizeImageFile(file);
    const form = new FormData();
    form.append('image', resized);
    form.append('label_id', String(labelId));
    await createSampleImage(dataset.id, form);
    onChanged();
  };
  const addSentence = async (labelId: number) => {
    const text = (sentenceDrafts[labelId] || '').trim();
    if (!text) return;
    if (atQuota) {
      message.error(`Limite atteinte (${MAX_SAMPLES_PER_DATASET} phrases maximum).`);
      return;
    }
    try {
      await createSampleText(dataset.id, { label_id: labelId, content_text: text });
      setSentenceDrafts((prev) => ({ ...prev, [labelId]: '' }));
      onChanged();
    } catch (error: any) {
      message.error(error?.response?.data || 'Échec de l’ajout.');
    }
  };
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text strong>
        Quels labels veux-tu apprendre à ton IA à reconnaître ? Ajoute-en au moins deux.
      </Typography.Text>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          aria-label="Nom du nouveau label"
          placeholder="Nouveau label"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={addLabel}
          disabled={!editable}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={addLabel} disabled={!editable}>
          Ajouter
        </Button>
      </Space.Compact>
      <Typography.Text type="secondary">
        {sampleCount} / {MAX_SAMPLES_PER_DATASET} {isText ? 'phrases' : 'images'}
      </Typography.Text>
      <List
        dataSource={dataset.labels || []}
        locale={{ emptyText: 'Ajoutez un premier label.' }}
        renderItem={(label) => (
          <List.Item
            actions={[
              isText ? (
                <Space.Compact key="sentence">
                  <Input
                    aria-label={`Nouvelle phrase pour ${label.name}`}
                    placeholder="Nouvelle phrase"
                    maxLength={MAX_TEXT_LENGTH}
                    value={sentenceDrafts[label.id] || ''}
                    onChange={(e) =>
                      setSentenceDrafts((prev) => ({ ...prev, [label.id]: e.target.value }))
                    }
                    onPressEnter={() => addSentence(label.id)}
                    disabled={!editable || atQuota}
                  />
                  <Button onClick={() => addSentence(label.id)} disabled={!editable || atQuota}>
                    Ajouter
                  </Button>
                </Space.Compact>
              ) : (
                <Upload
                key="upload"
                showUploadList={false}
                accept="image/*"
                disabled={!editable || atQuota}
                beforeUpload={(file) => {
                  if (atQuota) {
                    message.error(`Limite atteinte (${MAX_SAMPLES_PER_DATASET} images maximum).`);
                    return false;
                  }
                  if (file.size > MAX_IMAGE_BYTES) {
                    message.error('Image trop lourde (5 Mo maximum).');
                    return false;
                  }
                  upload(label.id, file).catch((error) =>
                    message.error(error?.response?.data || 'Échec du téléversement.'),
                  );
                  return false;
                }}
                >
                  <Button icon={<UploadOutlined />} disabled={!editable || atQuota}>
                    Image
                  </Button>
                </Upload>
              ),
              <Popconfirm
                key="delete"
                title="Supprimer ce label et ses images ?"
                onConfirm={() => deleteLabel(label.id).then(onChanged)}
                disabled={!editable}
              >
                <Button danger icon={<DeleteOutlined />} disabled={!editable} />
              </Popconfirm>,
            ]}
          >
            <Card
              size="small"
              title={
                editing === label.id ? (
                  <Input
                    autoFocus
                    disabled={!editable}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onPressEnter={() =>
                      updateLabel(label.id, { name: editingName, position: label.position }).then(() => {
                        setEditing(null);
                        onChanged();
                      })
                    }
                  />
                ) : (
                  <span>{label.name}</span>
                )
              }
              extra={
                editing === label.id ? (
                  <Button
                    type="link"
                    onClick={() =>
                      updateLabel(label.id, { name: editingName, position: label.position }).then(() => {
                        setEditing(null);
                        onChanged();
                      })
                    }
                    disabled={!editable}
                  >
                    OK
                  </Button>
                ) : (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditing(label.id);
                      setEditingName(label.name);
                    }}
                    aria-label={`Renommer ${label.name}`}
                    disabled={!editable}
                  />
                )
              }
            >
              {isText ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {(dataset.samples || [])
                    .filter((sample) => sample.label_id === label.id && sample.content_text)
                    .map((sample) => (
                      <Space
                        key={sample.id}
                        align="start"
                        style={{ width: '100%', justifyContent: 'space-between' }}
                      >
                        <Typography.Text>{sample.content_text}</Typography.Text>
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => deleteSample(sample.id).then(onChanged)}
                          disabled={!editable}
                        >
                          Supprimer
                        </Button>
                      </Space>
                    ))}
                </Space>
              ) : (
                <Space wrap>
                  {(dataset.samples || [])
                    .filter((sample) => sample.label_id === label.id && sample.content_url)
                    .map((sample) => (
                      <div key={sample.id}>
                        <img
                          src={getImagePath(sample.content_url || '')}
                          alt={label.name}
                          width={72}
                          height={72}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                        />
                        <br />
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => deleteSample(sample.id).then(onChanged)}
                          disabled={!editable}
                        >
                          Supprimer
                        </Button>
                      </div>
                    ))}
                </Space>
              )}
            </Card>
          </List.Item>
        )}
      />
    </Space>
  );
};
export default DatasetStation;
